import type { PublicTeamID } from '@common/ids';
import type { PaidAccessLevel } from '@common/paidAccessLevel';
import type { Season } from '@common/season';
import type { Sport } from '@common/sport';

export enum AgeDiscrete {
    EIGHT_AND_UNDER = '8U',
    NINE_AND_UNDER = '9U',
    TEN_AND_UNDER = '10U',
    ELEVEN_AND_UNDER = '11U',
    TWELVE_AND_UNDER = '12U',
    THIRTEEN_AND_UNDER = '13U',
    FOURTEEN_AND_UNDER = '14U',
    FIFTEEN_AND_UNDER = '15U',
    SIXTEEN_AND_UNDER = '16U',
    SEVENTEEN_AND_UNDER = '17U',
    EIGHTEEN_AND_UNDER = '18U',
    EIGHTEEN_AND_OVER = '18O',
}

export enum AgeRange {
    UNDER_THIRTEEN = 'Under 13',
    THIRTEEN_TO_EIGHTEEN = 'Between 13 - 18',
    OVER_EIGHTEEN = 'Over 18',
}

export enum AgeSchoolLevel {
    ELEMENTARY = 'elementary',
    MIDDLE_TWELVE_AND_UNDER = 'middle_12U',
    MIDDLE_THIRTEEN_AND_OVER = 'middle_13O',
    HIGH_FRESHMAN = 'high_freshman',
    HIGH_JUNIOR_VARSITY = 'high_junior_varsity',
    HIGH_VARSITY = 'high_varsity',
    COLLEGE = 'college',
}

export const AgeSchoolOptions = {
    HIGH_SCHOOL: 'high_school',
    MIDDLE_SCHOOL: 'middle_school',
    ELEMENTARY: AgeSchoolLevel.ELEMENTARY,
    COLLEGE: AgeSchoolLevel.COLLEGE,
} as const;

export type AgeSchoolOptionType = (typeof AgeSchoolOptions)[keyof typeof AgeSchoolOptions];

export type AgeGroup = AgeDiscrete | AgeRange | AgeSchoolLevel;

export enum NGB {
    LITTLE_LEAGUE = 'little_league',
    BABE_RUTH_CAL_RIPKEN_LEAGUE = 'babe_ruth_cal_ripken',
    USSSA = 'usssa',
    PONY = 'pony',
    AMERICAN_LEGION = 'american_legion',
    PERFECT_GAME = 'perfect_game',
    AAU = 'aau',
    USA_SOFTBALL = 'usa_softball',
    TRIPPLE_CROWN = 'triple_crown',
    PREMIER_GIRLS_FASTPITCH = 'premier_girls_fastpitch',
    ALLIANCE_FASTPITCH = 'alliance_fastpitch',
    MLB_RBI = 'mlb_rbi',
}

export interface PublicTeam {
    id: PublicTeamID;
    urlEncodedName: string; //safe name to use in the url
    name: string;
    sport: Sport;
    ageGroup: AgeGroup;
    avatarUrl: string;
    seasonName: Season;
    seasonYear: number;
    seasonRecord: { wins: number; losses: number; ties: number };
    location: {
        city?: string;
        state?: string;
        country?: string;
    };
    staff?: string[];
    paidAccessLevel?: PaidAccessLevel;
    ngb?: NGB[];
    playerCount?: number;
}

export const abbreviateName = (name: string): string => {
    if (name.startsWith('TBD')) {
        return 'TBD';
    }

    let shortName = name.toUpperCase().trim();
    if (shortName.startsWith('THE ')) {
        shortName = shortName.substring(3);
    }
    shortName = shortName.replace(/\s/g, '');
    if (shortName.length <= 4) {
        return shortName;
    }
    const firstCharacter = shortName.substring(0, 1);
    let restOfString = shortName.substring(1);
    restOfString = restOfString.replace(/[AEIOU]/g, '');
    let abbreviation = firstCharacter + restOfString;
    if (abbreviation.length > 4) {
        abbreviation = abbreviation.substring(0, 4);
    }
    return abbreviation;
};
